import { getApiBaseUrl } from "src/config/endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./httpUtils";

const API_BASE_URL = getApiBaseUrl();
const OVERVIEW_BASE_PATH = "paid-media-overview/dashboard";

export type PaidMediaOverviewPlatform = "meta_ads" | "google_ads";
export type PaidMediaOverviewPlatformFilter = "all" | PaidMediaOverviewPlatform;
export type PaidMediaOverviewAttributionMode = "platform_reported" | "shopify_validated";
export type PaidMediaOverviewGranularity = "daily" | "weekly" | "monthly";
export type PaidMediaOverviewSortOrder = "asc" | "desc";

export type PaidMediaOverviewQuadrant =
  | "SCALE_WINNER"
  | "GROWTH_OPPORTUNITY"
  | "HIGH_SPEND_LOW_RETURN"
  | "LOW_PRIORITY";

export interface PaidMediaOverviewWarning {
  code: string;
  message: string;
}

export interface PaidMediaOverviewPeriod {
  start_date: string | null;
  end_date: string | null;
}

export interface PaidMediaOverviewSourceAvailability {
  meta_ads?: boolean;
  google_ads?: boolean;
}

export interface PaidMediaOverviewMeta {
  currency?: string | null;
  attribution_mode?: PaidMediaOverviewAttributionMode | null;
  selected_period?: PaidMediaOverviewPeriod | null;
  prior_period?: PaidMediaOverviewPeriod | null;
  data_freshness_at?: string | null;
  source_availability?: PaidMediaOverviewSourceAvailability | null;
  shopify_available?: boolean | null;
  sort_by?: string | null;
  sort_order?: PaidMediaOverviewSortOrder | null;
  warnings?: PaidMediaOverviewWarning[] | null;
}

interface PaidMediaOverviewEnvelope<TData> {
  data: TData;
  meta?: PaidMediaOverviewMeta | null;
}

export interface PaidMediaOverviewDateRangeParams {
  start_date: string;
  end_date: string;
  /**
   * Response language. Every overview endpoint returns copy the dashboard renders verbatim —
   * `leading_platform.reason`, `trends[].leader_reason`, campaign `status_label`/`status_reason`,
   * `confidence_label`, the filter option labels and `meta.warnings[].message` — so a request
   * without it leaves those strings English on the Arabic dashboard.
   */
  lang?: ApiLang | string | null;
}

export interface PaidMediaOverviewFilterParams {
  platform?: PaidMediaOverviewPlatformFilter;
  campaign_id?: string;
  objective_or_network?: string;
  status?: string;
}


export interface PaidMediaOverviewSummaryKpi {
  id: string;
  title: string;
  value: number | null;
  formatted_value: string | null;
  previous_value: number | null;
  percent_change: number | null;
  /** Localized change string, e.g. "14.29%" / "١٤.٢٩%". */
  formatted_percent_change?: string | null;
}

export interface PaidMediaOverviewLeadingPlatform {
  platform: PaidMediaOverviewPlatform | null;
  roas: number | null;
  reason: string | null;
}

export interface PaidMediaOverviewRevenueContributionLeader {
  platform: PaidMediaOverviewPlatform | null;
  revenue_share: number | null;
  spend_share: number | null;
  efficiency_index: number | null;
}

export interface PaidMediaOverviewEfficiencyGap {
  value: number | null;
  leading_platform: PaidMediaOverviewPlatform | null;
}

export interface PaidMediaOverviewSummaryPlatform {
  platform: PaidMediaOverviewPlatform;
  available: boolean;
  spend: number | null;
  formatted_spend: string | null;
  revenue: number | null;
  formatted_revenue: string | null;
  purchases: number | null;
  formatted_purchases: string | null;
  roas: number | null;
  formatted_roas: string | null;
  cost_per_purchase: number | null;
  formatted_cost_per_purchase: string | null;
  spend_share: number | null;
  formatted_spend_share: string | null;
  revenue_share: number | null;
  formatted_revenue_share: string | null;
  purchase_share: number | null;
  formatted_purchase_share: string | null;
  efficiency_index: number | null;
  formatted_efficiency_index: string | null;
}

export interface PaidMediaOverviewSummaryData {
  combined: PaidMediaOverviewSummaryKpi[];
  leading_platform: PaidMediaOverviewLeadingPlatform | null;
  revenue_contribution_leader: PaidMediaOverviewRevenueContributionLeader | null;
  efficiency_gap: PaidMediaOverviewEfficiencyGap | null;
  platforms: PaidMediaOverviewSummaryPlatform[];
}

export type PaidMediaOverviewSummaryResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewSummaryData>;

export interface PaidMediaOverviewSummaryParams
  extends PaidMediaOverviewDateRangeParams,
    PaidMediaOverviewFilterParams {
  attribution_mode?: PaidMediaOverviewAttributionMode;
}


export interface PaidMediaOverviewTrendMetrics {
  spend: number | null;
  formatted_spend: string | null;
  revenue: number | null;
  formatted_revenue: string | null;
  purchases: number | null;
  formatted_purchases: string | null;
  roas: number | null;
  formatted_roas: string | null;
  cost_per_purchase: number | null;
  formatted_cost_per_purchase: string | null;
}

export interface PaidMediaOverviewTrendPoint {
  date: string;
  meta_ads: PaidMediaOverviewTrendMetrics | null;
  google_ads: PaidMediaOverviewTrendMetrics | null;
  combined: PaidMediaOverviewTrendMetrics | null;
  leader: PaidMediaOverviewPlatform | null;
  leader_reason: string | null;
}

export interface PaidMediaOverviewTrendsData {
  granularity: PaidMediaOverviewGranularity;
  series: PaidMediaOverviewTrendPoint[];
}

export type PaidMediaOverviewTrendsResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewTrendsData>;

export interface PaidMediaOverviewTrendsParams
  extends PaidMediaOverviewDateRangeParams,
    PaidMediaOverviewFilterParams {
  granularity?: PaidMediaOverviewGranularity;
  attribution_mode?: PaidMediaOverviewAttributionMode;
}


export interface PaidMediaOverviewFunnelPlatform {
  platform: PaidMediaOverviewPlatform;
  available: boolean;
  impressions: number | null;
  formatted_impressions: string | null;
  clicks: number | null;
  formatted_clicks: string | null;
  purchases: number | null;
  formatted_purchases: string | null;
  ctr: number | null;
  formatted_ctr: string | null;
  click_to_purchase_rate: number | null;
  formatted_click_to_purchase_rate: string | null;
}

export interface PaidMediaOverviewFunnelData {
  platforms: PaidMediaOverviewFunnelPlatform[];
}

export type PaidMediaOverviewFunnelResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewFunnelData>;

export interface PaidMediaOverviewFunnelParams
  extends PaidMediaOverviewDateRangeParams,
    PaidMediaOverviewFilterParams {
  attribution_mode?: PaidMediaOverviewAttributionMode;
}


export interface PaidMediaOverviewScalePoint {
  platform: PaidMediaOverviewPlatform;
  label: string;
  campaign_id: string | null;
  spend: number | null;
  formatted_spend: string | null;
  revenue: number | null;
  formatted_revenue: string | null;
  roas: number | null;
  formatted_roas: string | null;
  quadrant: PaidMediaOverviewQuadrant | null;
}

export interface PaidMediaOverviewScaleBenchmarks {
  spend_benchmark: number | null;
  roas_benchmark: number | null;
  roas_benchmark_source: string | null;
}

export interface PaidMediaOverviewEfficiencyScaleData {
  platform_view: PaidMediaOverviewScalePoint[];
  campaign_view: PaidMediaOverviewScalePoint[];
  benchmarks: PaidMediaOverviewScaleBenchmarks | null;
}

export type PaidMediaOverviewEfficiencyScaleResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewEfficiencyScaleData>;

export interface PaidMediaOverviewEfficiencyScaleParams
  extends PaidMediaOverviewDateRangeParams,
    PaidMediaOverviewFilterParams {
  attribution_mode?: PaidMediaOverviewAttributionMode;
}

export interface PaidMediaOverviewRevenueValidationPlatform {
  platform: PaidMediaOverviewPlatform;
  available: boolean;
  platform_reported_revenue: number | null;
  formatted_platform_reported_revenue: string | null;
  shopify_gross_revenue: number | null;
  formatted_shopify_gross_revenue: string | null;
  shopify_net_revenue: number | null;
  formatted_shopify_net_revenue: string | null;
  reporting_variance: number | null;
  formatted_reporting_variance: string | null;
  refund_adjusted_roas: number | null;
  formatted_refund_adjusted_roas: string | null;
}

export interface PaidMediaOverviewRevenueValidationData {
  platforms: PaidMediaOverviewRevenueValidationPlatform[];
}

export type PaidMediaOverviewRevenueValidationResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewRevenueValidationData>;

export interface PaidMediaOverviewRevenueValidationParams
  extends PaidMediaOverviewDateRangeParams,
    PaidMediaOverviewFilterParams {}


export interface PaidMediaOverviewCampaignRow {
  platform: PaidMediaOverviewPlatform;
  campaign_id: string;
  campaign_name: string;
  objective_or_network: string | null;
  start_date: string | null;
  spend: number | null;
  formatted_spend: string | null;
  impressions: number | null;
  formatted_impressions: string | null;
  clicks: number | null;
  formatted_clicks: string | null;
  ctr: number | null;
  formatted_ctr: string | null;
  purchases: number | null;
  formatted_purchases: string | null;
  revenue: number | null;
  formatted_revenue: string | null;
  roas: number | null;
  formatted_roas: string | null;
  cost_per_purchase: number | null;
  formatted_cost_per_purchase: string | null;
  spend_share: number | null;
  formatted_spend_share: string | null;
  revenue_share: number | null;
  formatted_revenue_share: string | null;
  efficiency_index: number | null;
  formatted_efficiency_index: string | null;
  status_code: string | null;
  status_label: string | null;
  status_reason: string | null;
  data_sufficiency: string | null;
  benchmark_roas: number | null;
  formatted_benchmark_roas: string | null;
  benchmark_cpa: number | null;
  formatted_benchmark_cpa: string | null;
}

export interface PaidMediaOverviewCampaignBenchmarks {
  spend_p75: number | null;
  spend_median: number | null;
  revenue_p75: number | null;
  purchases_p75: number | null;
  roas_p75: number | null;
  roas_p25: number | null;
  target_roas: number | null;
  cpa_p75: number | null;
  target_cpa: number | null;
  benchmark_source: string | null;
}

export interface PaidMediaOverviewCampaignsData {
  rows: PaidMediaOverviewCampaignRow[];
  benchmarks: PaidMediaOverviewCampaignBenchmarks | null;
  total_rows: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export type PaidMediaOverviewCampaignsResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewCampaignsData>;

export interface PaidMediaOverviewCampaignsParams
  extends PaidMediaOverviewDateRangeParams,
    PaidMediaOverviewFilterParams {
  search?: string;
  sort_by?: string;
  sort_order?: PaidMediaOverviewSortOrder;
  page?: number;
  page_size?: number;
}


export interface PaidMediaOverviewCampaignDetailHeader {
  platform: PaidMediaOverviewPlatform;
  campaign_id: string;
  campaign_name: string;
  objective_or_network: string | null;
  start_date: string | null;
  end_date: string | null;
}

export interface PaidMediaOverviewCampaignDetailKpis {
  spend: number | null;
  formatted_spend: string | null;
  revenue: number | null;
  formatted_revenue: string | null;
  purchases: number | null;
  formatted_purchases: string | null;
  impressions: number | null;
  formatted_impressions: string | null;
  clicks: number | null;
  formatted_clicks: string | null;
  ctr: number | null;
  formatted_ctr: string | null;
  roas: number | null;
  formatted_roas: string | null;
  cost_per_purchase: number | null;
  formatted_cost_per_purchase: string | null;
}

export interface PaidMediaOverviewCampaignDetailTrendPoint {
  date: string;
  spend: number | null;
  formatted_spend: string | null;
  revenue: number | null;
  formatted_revenue: string | null;
}

export interface PaidMediaOverviewCampaignDetailTrend {
  granularity: PaidMediaOverviewGranularity;
  series: PaidMediaOverviewCampaignDetailTrendPoint[];
}

export interface PaidMediaOverviewCampaignDetailData {
  header: PaidMediaOverviewCampaignDetailHeader | null;
  kpis: PaidMediaOverviewCampaignDetailKpis | null;
  trend: PaidMediaOverviewCampaignDetailTrend | null;
  insight: string | null;
}

export type PaidMediaOverviewCampaignDetailResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewCampaignDetailData>;

export interface PaidMediaOverviewCampaignDetailParams extends PaidMediaOverviewDateRangeParams {
  platform: PaidMediaOverviewPlatform;
  campaign_id: string;
  granularity?: PaidMediaOverviewGranularity;
  attribution_mode?: PaidMediaOverviewAttributionMode;
}


export interface PaidMediaOverviewAttributionComponent {
  orders: number | null;
  formatted_orders: string | null;
  percentage: number | null;
  formatted_percentage: string | null;
  weight: number | null;
}

export interface PaidMediaOverviewAttributionComponents {
  recognized_source: PaidMediaOverviewAttributionComponent;
  campaign_id: PaidMediaOverviewAttributionComponent;
  ad_id: PaidMediaOverviewAttributionComponent;
  usable_utm: PaidMediaOverviewAttributionComponent;
}

export interface PaidMediaOverviewAttributionConfidenceData {
  eligible_paid_media_orders: number;
  formatted_eligible_paid_media_orders: string | null;
  attribution_confidence: number | null;
  formatted_attribution_confidence: string | null;
  confidence_label: string | null;
  components: PaidMediaOverviewAttributionComponents;
}

export type PaidMediaOverviewAttributionConfidenceResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewAttributionConfidenceData>;

// Deliberately NOT `PaidMediaOverviewFilterParams` — this endpoint only supports `platform`.
// It scores the whole account, not a per-campaign slice, so `campaign_id`/`objective_or_network`/
// `status` don't apply here even though every other overview endpoint accepts them.
export interface PaidMediaOverviewAttributionConfidenceParams
  extends PaidMediaOverviewDateRangeParams {
  platform?: PaidMediaOverviewPlatformFilter;
}


export interface PaidMediaOverviewPlatformOption {
  value: PaidMediaOverviewPlatform;
  label: string;
  available: boolean;
}

export interface PaidMediaOverviewCampaignOption {
  platform: PaidMediaOverviewPlatform;
  campaign_id: string;
  campaign_name: string;
}

export interface PaidMediaOverviewStatusOption {
  value: string;
  label: string;
}

export interface PaidMediaOverviewFiltersData {
  platforms: PaidMediaOverviewPlatformOption[];
  campaigns: PaidMediaOverviewCampaignOption[];
  objectives_or_networks: string[];
  statuses: PaidMediaOverviewStatusOption[];
}

export type PaidMediaOverviewFiltersResponse =
  PaidMediaOverviewEnvelope<PaidMediaOverviewFiltersData>;

// Only `platform` narrows this endpoint: it scopes `data.campaigns` to the selected platform,
// while `data.platforms` always lists both regardless of the filter.
export interface PaidMediaOverviewFiltersParams extends PaidMediaOverviewDateRangeParams {
  platform?: PaidMediaOverviewPlatformFilter;
}


const buildSearchParams = (
  params: Record<string, string | number | undefined | null>
): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    const serialized = typeof value === "number" ? String(value) : value.trim();
    if (serialized) {
      searchParams.set(key, serialized);
    }
  });

  return searchParams;
};

const dateRangeSearchParams = (params: PaidMediaOverviewDateRangeParams) => ({
  start_date: params.start_date,
  end_date: params.end_date,
  lang: normalizeApiLang(params.lang),
});

const filterSearchParams = (
  params: PaidMediaOverviewFilterParams
): Record<string, string | undefined> => ({
  platform: params.platform,
  campaign_id: params.campaign_id,
  objective_or_network: params.objective_or_network,
  status: params.status,
});

const roundOverviewNumbers = <TValue,>(value: TValue): TValue => {
  if (typeof value === "number") {
    return (Number.isFinite(value) ? Math.round(value * 100) / 100 : value) as TValue;
  }
  if (Array.isArray(value)) {
    return value.map(roundOverviewNumbers) as TValue;
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, roundOverviewNumbers(entry)])
    ) as TValue;
  }
  return value;
};

const requestOverview = async <TResponse,>(
  path: string,
  searchParams: URLSearchParams
): Promise<TResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/${OVERVIEW_BASE_PATH}/${path}?${searchParams.toString()}`,
    {
      method: "GET",
      headers: buildAuthHeaders("api_key"),
    }
  );

  const data = await handleJsonResponse<TResponse>(response);
  return roundOverviewNumbers(data);
};


export const paidMediaOverviewService = {
  fetchSummary: (params: PaidMediaOverviewSummaryParams) =>
    requestOverview<PaidMediaOverviewSummaryResponse>(
      "summary",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        attribution_mode: params.attribution_mode,
        ...filterSearchParams(params),
      })
    ),

  fetchTrends: (params: PaidMediaOverviewTrendsParams) =>
    requestOverview<PaidMediaOverviewTrendsResponse>(
      "trends",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        granularity: params.granularity,
        attribution_mode: params.attribution_mode,
        ...filterSearchParams(params),
      })
    ),

  fetchFunnel: (params: PaidMediaOverviewFunnelParams) =>
    requestOverview<PaidMediaOverviewFunnelResponse>(
      "funnel",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        attribution_mode: params.attribution_mode,
        ...filterSearchParams(params),
      })
    ),

  fetchEfficiencyScale: (params: PaidMediaOverviewEfficiencyScaleParams) =>
    requestOverview<PaidMediaOverviewEfficiencyScaleResponse>(
      "efficiency-scale",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        attribution_mode: params.attribution_mode,
        ...filterSearchParams(params),
      })
    ),

  fetchRevenueValidation: (params: PaidMediaOverviewRevenueValidationParams) =>
    requestOverview<PaidMediaOverviewRevenueValidationResponse>(
      "revenue-validation",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        ...filterSearchParams(params),
      })
    ),

  fetchCampaigns: (params: PaidMediaOverviewCampaignsParams) =>
    requestOverview<PaidMediaOverviewCampaignsResponse>(
      "campaigns",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        ...filterSearchParams(params),
        search: params.search,
        sort_by: params.sort_by,
        sort_order: params.sort_order,
        page: params.page,
        page_size: params.page_size,
      })
    ),

  fetchCampaignDetail: (params: PaidMediaOverviewCampaignDetailParams) =>
    requestOverview<PaidMediaOverviewCampaignDetailResponse>(
      `campaigns/${encodeURIComponent(params.platform)}/${encodeURIComponent(params.campaign_id)}`,
      buildSearchParams({
        ...dateRangeSearchParams(params),
        granularity: params.granularity,
        attribution_mode: params.attribution_mode,
      })
    ),

  fetchAttributionConfidence: (params: PaidMediaOverviewAttributionConfidenceParams) =>
    requestOverview<PaidMediaOverviewAttributionConfidenceResponse>(
      "attribution-confidence",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        platform: params.platform,
      })
    ),

  fetchFilters: (params: PaidMediaOverviewFiltersParams) =>
    requestOverview<PaidMediaOverviewFiltersResponse>(
      "filters",
      buildSearchParams({
        ...dateRangeSearchParams(params),
        platform: params.platform,
      })
    ),
};
